import { useEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { useI18n } from '../../i18n';
import { DEFAULT_SKIN, type TiaSkin } from './skins';

/**
 * Tia's stage (mockup tia-stage-preview.html `.stage`): five layers — backdrop · particles · Tia · accessory · frame —
 * with her name and status on top-left, a speech bubble, and ▴ ▾ for the size. The look comes from a skin (data,
 * ./skins.ts); this code does not know any set. Four sizes: «over the edge» < strip < normal < tall.
 * Colours are the stage's own (a skin, not a theme).
 */
export type TiaStageState = 'idle' | 'thinking' | 'done' | 'error' | 'notes';
export type TiaStageSize = 'normal' | 'tall' | 'strip' | 'perch';

/** Smaller to bigger: ▴ goes one step down this list, ▾ one step up. */
export const TIA_STAGE_SIZES: readonly TiaStageSize[] = ['perch', 'strip', 'normal', 'tall'];

export interface TiaStageProps {
  state: TiaStageState;
  size: TiaStageSize;
  onSizeChange: (size: TiaStageSize) => void;
  name: string;
  statusText: string;
  say: string;
  /** A click on the line (the «N remarks. Show them?» one opens the panel). */
  onSayClick?: (() => void) | undefined;
  /** The pose (layer 3); absent — the initial of a persona that has no art. */
  portraitSrc?: string | undefined;
  portraitInitial?: string | undefined;
  portraitColor?: string | undefined;
  /** Extra buttons in the tools row, before ▴ ▾ (the Studio column puts its «fold the column» here). */
  trailing?: ReactNode;
  /** The face layer over the pose (an emotion); absent — the pose as it is. */
  faceSrc?: string | undefined;
  skin?: TiaSkin | undefined;
}

function useReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const read = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [reduced, setReduced] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = (): void => setReduced(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

interface Particle { x: number; y: number; v: number; a: number; s: number; r: number }

function spawn(): Particle {
  return { x: Math.random(), y: 1 + Math.random() * 0.2, v: 0.3 + Math.random() * 0.7, a: Math.random() * Math.PI * 2, s: 0.6 + Math.random() * 0.8, r: Math.random() * 6.28 };
}

function drawRune(ctx: CanvasRenderingContext2D, x: number, y: number, k: number, rot: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.arc(0, 0, 6 * k, 0, 6.28);
  ctx.moveTo(0, -5 * k);
  for (let i = 1; i <= 8; i++) {
    const ang = (i * Math.PI) / 4 - Math.PI / 2;
    const rr = i % 2 ? 1.8 * k : 5 * k;
    ctx.lineTo(Math.cos(ang) * rr, Math.sin(ang) * rr);
  }
  ctx.stroke();
  ctx.restore();
}

/** The particle layer: the buffer is set to the box × devicePixelRatio EVERY frame, so a stage that changes height never stretches it. */
function Particles({ skin, state, speed }: { skin: TiaSkin; state: TiaStageState; speed: number }): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);
  const live = useRef({ skin, state, speed, burst: 0 });
  live.current.skin = skin;
  live.current.speed = speed;
  useEffect(() => {
    const prev = live.current.state;
    live.current.state = state;
    if (state === 'done' && prev !== 'done') live.current.burst = 50;
  }, [state]);
  useEffect(() => {
    const cv = ref.current;
    if (cv === null || typeof cv.getContext !== 'function') return;
    const ctx = cv.getContext('2d');
    if (ctx === null) return;
    const parts: Particle[] = [];
    for (let i = 0; i < 46; i++) { const p = spawn(); p.y = Math.random(); parts.push(p); }
    let raf = 0;
    const frame = (): void => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(cv.clientWidth * dpr));
      const h = Math.max(1, Math.round(cv.clientHeight * dpr));
      if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
      ctx.clearRect(0, 0, w, h);
      const { skin: sk, state: st, speed: mult } = live.current;
      const p0 = sk.particle;
      const speedNow = p0.speed * mult;
      if (live.current.burst > 0) live.current.burst -= 1;
      const burst = live.current.burst > 0;
      for (const p of parts) {
        p.y -= speedNow * p.v * 0.0025 * (burst ? 4 : 1);
        p.x += Math.sin((p.y + p.a) * 6) * 0.0006 * (p0.kind === 'leaf' ? 3 : 1);
        p.r += 0.01 * speedNow;
        if (p.y < -0.05) Object.assign(p, spawn());
        const x = p.x * w;
        const y = p.y * h;
        const alpha = Math.min(1, (1 - p.y) * 1.4) * (st === 'thinking' ? 0.9 : 0.55);
        const color = `rgba(${p0.color},${alpha})`;
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = dpr;
        if (p0.kind === 'rune') drawRune(ctx, x, y, p.s * dpr, p.r);
        else if (p0.kind === 'leaf') {
          ctx.save(); ctx.translate(x, y); ctx.rotate(p.r); ctx.beginPath();
          ctx.ellipse(0, 0, 4 * p.s * dpr, 2 * p.s * dpr, 0, 0, 6.28); ctx.fill(); ctx.restore();
        } else { ctx.beginPath(); ctx.arc(x, y, (p0.kind === 'dust' ? 1 : 1.6) * p.s * dpr, 0, 6.28); ctx.fill(); }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      data-testid="tia-stage-particles"
      data-speed={String(speed)}
      className="absolute inset-0 z-[1] h-full w-full"
    />
  );
}

const STAGE_HEIGHT: Record<TiaStageSize, string> = {
  normal: 'h-[300px]',
  tall: 'h-[460px]',
  strip: 'h-[84px]',
  perch: 'h-0 overflow-visible',
};

const FIGURE: Record<TiaStageSize, string> = {
  normal: 'bottom-[-6px] left-1/2 size-[250px] -translate-x-1/2',
  tall: 'bottom-[-6px] left-1/2 size-[340px] -translate-x-1/2',
  strip: 'bottom-1 left-[52px] size-[76px] -translate-x-1/2',
  perch: 'right-4 top-[-52px] size-[96px]',
};

const NAME_POS: Record<TiaStageSize, string> = {
  normal: 'left-3 top-2.5',
  tall: 'left-3 top-2.5',
  strip: 'left-[98px] top-5',
  perch: 'left-3 top-[-44px]',
};

const SAY_POS: Record<TiaStageSize, string> = {
  normal: 'right-2.5 top-[46px] max-w-[150px]',
  tall: 'left-3 top-[58px] max-w-[160px]',
  strip: 'hidden',
  perch: 'hidden',
};

// The ring of the alcove by state: the states' own colours (mockup --ok / --err / --warn and the stage's teal), never the app accent.
const RING: Record<TiaStageState, string> = {
  idle: 'inset 0 0 0 2px transparent',
  thinking: 'inset 0 0 0 2px #7fe3ea, inset 0 0 30px rgba(127,227,234,.35)',
  done: 'inset 0 0 0 2px #34d399',
  error: 'inset 0 0 0 2px #fb7185',
  notes: 'inset 0 0 0 2px #fbbf24',
};

export default function TiaStage({ state, size, onSizeChange, name, statusText, say, onSayClick, portraitSrc, portraitInitial, portraitColor, faceSrc, skin = DEFAULT_SKIN, trailing }: TiaStageProps): JSX.Element {
  const { t } = useI18n();
  const reduced = useReducedMotion();
  const speed = reduced ? 0 : state === 'thinking' ? 3 : 1;
  const idx = TIA_STAGE_SIZES.indexOf(size);
  const smaller = TIA_STAGE_SIZES[Math.max(0, idx - 1)]!;
  const bigger = TIA_STAGE_SIZES[Math.min(TIA_STAGE_SIZES.length - 1, idx + 1)]!;
  const perch = size === 'perch';
  const strip = size === 'strip';
  const [faceOk, setFaceOk] = useState(false);
  useEffect(() => { setFaceOk(false); }, [faceSrc]);
  const imgAnim = reduced ? '' : state === 'done' ? 'tia-breathe tia-nod' : 'tia-breathe';

  return (
    <div
      data-testid="tia-stage"
      data-state={state}
      data-size={size}
      className={`relative z-[5] shrink-0 border-b border-line text-fg [isolation:isolate] transition-[height] duration-300 ${perch ? '' : 'overflow-hidden'} ${STAGE_HEIGHT[size]}${perch ? ' mt-[60px] border-b-0' : ''}`}
    >
      <div
        data-testid="tia-stage-backdrop"
        className={`absolute inset-0 z-0${perch ? ' hidden' : ''}`}
        style={{ background: skin.backdrop }}
      />
      <Particles skin={skin} state={state} speed={speed} />
      <div
        data-testid="tia-stage-figure"
        className={`absolute z-[2] transition-all duration-300 ${FIGURE[size]}`}
      >
        <div
          className={`absolute inset-0 overflow-hidden transition-colors duration-300 ${strip || perch ? 'rounded-full' : 'rounded-t-full'} ${perch ? 'shadow-[0_0_0_3px_var(--color-neutral-900),0_0_0_4px_var(--color-neutral-800)]' : ''}`}
          style={{ background: skin.tint }}
        >
          {portraitSrc !== undefined ? (
            <img
              src={portraitSrc}
              alt={name}
              className={`block h-full w-full object-cover object-[50%_18%] mix-blend-multiply [transform-origin:50%_100%] ${imgAnim}`}
            />
          ) : (
            <div
              role="img"
              aria-label={name}
              className="flex h-full w-full items-center justify-center text-5xl font-semibold text-fg"
              style={{ backgroundColor: portraitColor }}
            >
              {portraitInitial}
            </div>
          )}
          {faceSrc !== undefined ? (
            <img
              src={faceSrc}
              alt=""
              aria-hidden="true"
              onLoad={(e): void => setFaceOk(e.currentTarget.naturalWidth >= 64)}
              onError={(): void => setFaceOk(false)}
              className={`absolute left-[30%] top-[24%] w-[40%] object-cover ${faceOk ? '' : 'hidden'}`}
            />
          ) : null}
          <span data-testid="tia-stage-ring" className="pointer-events-none absolute inset-0 rounded-[inherit] transition-shadow duration-300" style={{ boxShadow: RING[state] }} />
        </div>
      </div>
      <div
        data-testid="tia-stage-accessory"
        className={`pointer-events-none absolute inset-0 z-[3]${strip || perch ? ' hidden' : ''}`}
        dangerouslySetInnerHTML={{ __html: skin.accessory }}
      />
      <div
        data-testid="tia-stage-frame"
        className={`pointer-events-none absolute inset-0 z-[4]${perch ? ' hidden' : ''}`}
        style={{ boxShadow: skin.frame }}
      />
      <div className={`absolute z-[5] flex flex-col ${NAME_POS[size]}`}>
        <b data-testid="tia-stage-name" className="text-title font-semibold [text-shadow:0_1px_6px_rgba(0,0,0,.6)]">{name}</b>
        <span data-testid="tia-stage-status" className="text-meta text-own [text-shadow:0_1px_6px_rgba(0,0,0,.7)]">{statusText}</span>
      </div>
      {say !== '' ? (
        <div
          data-testid="tia-stage-say"
          role={onSayClick !== undefined ? 'button' : undefined}
          tabIndex={onSayClick !== undefined ? 0 : undefined}
          onClick={onSayClick}
          onKeyDown={onSayClick === undefined ? undefined : (e): void => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSayClick(); } }}
          className={`absolute z-[5] rounded-[10px_10px_10px_2px] border border-line bg-surface/80 px-2 py-1.5 text-ui ${reduced ? '' : 'tia-rise'} ${onSayClick !== undefined ? 'cursor-pointer hover:border-line' : ''} ${SAY_POS[size]}`}
        >
          {say}
        </div>
      ) : null}
      <div className={`absolute z-[5] flex gap-1 ${perch ? 'right-[120px] top-[-50px]' : 'right-2 top-2'}`}>
        {trailing}
        <button
          type="button"
          aria-label={t('tia.collapse')}
          title={t('tia.collapse')}
          disabled={idx <= 0}
          onClick={(): void => onSizeChange(smaller)}
          className="size-[26px] rounded bg-surface/60 text-body-s text-muted-foreground hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
        >
          {'▴'}
        </button>
        <button
          type="button"
          aria-label={t('tia.expand')}
          title={t('tia.expand')}
          disabled={idx >= TIA_STAGE_SIZES.length - 1}
          onClick={(): void => onSizeChange(bigger)}
          className="size-[26px] rounded bg-surface/60 text-body-s text-muted-foreground hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
        >
          {'▾'}
        </button>
      </div>
    </div>
  );
}
